import type {
  AccountSummary,
  ActiveOrder,
  CompanyProfile,
  ConnectionStatus,
  Dividend,
  HistoricalOrder,
  IndicatorSnapshot,
  Instrument,
  NewsItem,
  Position,
  RuleConfig,
  Signal,
  Snapshot,
  Transaction,
  Violation,
} from '@t212/shared';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export type PositionWithTv = Position & { tvSymbol: string };

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`GET ${path} -> ${res.status}`);
  return (await res.json()) as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST ${path} -> ${res.status}`);
  return (await res.json()) as T;
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PATCH ${path} -> ${res.status}`);
  return (await res.json()) as T;
}

export const api = {
  status: () => get<ConnectionStatus>('/api/status'),
  accountSummary: () => get<AccountSummary>('/api/account/summary'),
  positions: () => get<PositionWithTv[]>('/api/positions'),
  position: (ticker: string) => get<PositionWithTv>(`/api/positions/${encodeURIComponent(ticker)}`),
  activeOrders: () => get<ActiveOrder[]>('/api/orders/active'),
  orderHistory: (cursor?: string) =>
    get<{ items: HistoricalOrder[]; nextCursor?: string }>(
      `/api/history/orders${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  dividendHistory: (cursor?: string) =>
    get<{ items: Dividend[]; nextCursor?: string }>(
      `/api/history/dividends${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  transactionHistory: (cursor?: string) =>
    get<{ items: Transaction[]; nextCursor?: string }>(
      `/api/history/transactions${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  rules: () => get<RuleConfig[]>('/api/rules'),
  patchRule: (id: string, body: Partial<RuleConfig>) =>
    patch<RuleConfig>(`/api/rules/${encodeURIComponent(id)}`, body),
  violations: () => get<{ violations: Violation[] }>('/api/rules/violations'),
  snapshots: () => get<{ snapshots: Snapshot[] }>('/api/snapshots'),
  takeSnapshot: () => post<{ id: number }>('/api/snapshots/take', {}),
  news: (ticker: string) => get<{ items: NewsItem[] }>(`/api/news/${encodeURIComponent(ticker)}`),
  profile: (ticker: string) =>
    get<CompanyProfile>(`/api/profile/${encodeURIComponent(ticker)}`),
  instruments: () =>
    get<(Instrument & { tvSymbol: string })[]>('/api/market/instruments'),
  indicator: (ticker: string) =>
    get<IndicatorSnapshot>(`/api/indicators/${encodeURIComponent(ticker)}`),
  indicators: () => get<{ indicators: IndicatorSnapshot[] }>('/api/indicators'),
  signals: () => get<{ signals: Signal[] }>('/api/signals'),
  candles: (ticker: string, days?: number) =>
    get<{
      ticker: string;
      points: { t: number; c: number }[];
      ohlc: { t: number; o: number; h: number; l: number; c: number; v: number }[];
    }>(`/api/candles/${encodeURIComponent(ticker)}${days ? `?days=${days}` : ''}`),
  watchlist: () =>
    get<{
      items: {
        ticker: string;
        name: string;
        currency: string;
        sector: string | null;
        currentPrice: number;
        tvSymbol: string;
        addedAt: string;
      }[];
    }>('/api/watchlist'),
  addWatch: (ticker: string) => post<{ ok: true }>('/api/watchlist', { ticker }),
  removeWatch: async (ticker: string) => {
    const res = await fetch(`${API_BASE}/api/watchlist/${encodeURIComponent(ticker)}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error('watchlist delete failed');
    return (await res.json()) as { ok: true };
  },
  tradeStats: () =>
    get<{
      total: number;
      closed: number;
      winners: number;
      losers: number;
      winRate: number;
      avgWinner: number;
      avgLoser: number;
      payoffRatio: number;
      expectancy: number;
      profitFactor: number;
      netResult: number;
      riskOfRuinPct: number;
      kellyFraction: number;
    }>('/api/trade-stats'),
  holdingPeriods: () =>
    get<{
      buckets: {
        label: string;
        minDays: number;
        maxDays: number;
        winners: number;
        losers: number;
        avgWinR: number;
        avgLossR: number;
      }[];
    }>('/api/holding-periods'),
  setupBuckets: () =>
    get<{
      buckets: {
        tag: string;
        stats: {
          total: number;
          closed: number;
          winners: number;
          losers: number;
          winRate: number;
          avgWinner: number;
          avgLoser: number;
          payoffRatio: number;
          expectancy: number;
          profitFactor: number;
          netResult: number;
          riskOfRuinPct: number;
          kellyFraction: number;
        };
      }[];
    }>('/api/setup-buckets'),
  mistakes: () =>
    get<{
      items: { orderId: number; tag: string; note: string; createdAt: string }[];
      counts: Record<string, number>;
    }>('/api/mistakes'),
  mistakeTags: () => get<{ tags: string[] }>('/api/mistakes/_tags'),
  getMistakesFor: (orderId: number) =>
    get<{ orderId: number; tags: string[]; note: string }>(`/api/mistakes/${orderId}`),
  putMistakes: async (orderId: number, body: { tags: string[]; note?: string }) => {
    const res = await fetch(`${API_BASE}/api/mistakes/${orderId}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('mistakes save failed');
    return (await res.json()) as { orderId: number; tags: string[]; note: string };
  },
  performance: () =>
    get<{
      returns: {
        simple: number;
        twrr: number;
        mwrr: number;
        totalNetDeposits: number;
        trueGain: number;
      };
      risk: {
        volatility: number;
        sharpe: number;
        sortino: number;
        maxDrawdown: number;
        beta: number;
        dailyReturns: { date: string; ret: number; value: number }[];
      };
      tilt: {
        level: 'calm' | 'elevated' | 'tilt';
        recentSigma: number;
        baselineSigma: number;
        ratio: number;
        recentMaxDrawdown: number;
        message: string;
      };
    }>('/api/performance'),
  upcomingEarnings: () =>
    get<{
      items: {
        ticker: string;
        symbol: string;
        date: string;
        hour?: string;
        epsEstimate?: number;
      }[];
    }>('/api/earnings/upcoming'),
  earningsForTicker: (ticker: string) =>
    get<{
      items: { ticker: string; symbol: string; date: string; hour?: string; epsEstimate?: number }[];
    }>(`/api/earnings/${encodeURIComponent(ticker)}`),
  getJournal: (ticker: string) =>
    get<{ ticker: string; setupTag: string | null; rationale: string; updatedAt: string | null }>(
      `/api/journal/${encodeURIComponent(ticker)}`,
    ),
  putJournal: async (
    ticker: string,
    body: { setupTag?: string | null; rationale?: string },
  ) => {
    const res = await fetch(`${API_BASE}/api/journal/${encodeURIComponent(ticker)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('journal save failed');
    return (await res.json()) as {
      ticker: string;
      setupTag: string | null;
      rationale: string;
      updatedAt: string;
    };
  },
  previewOrder: (body: {
    ticker: string;
    side: 'BUY' | 'SELL';
    type: 'MARKET' | 'LIMIT' | 'STOP' | 'STOP_LIMIT';
    quantity: number;
    limitPrice?: number;
    stopPrice?: number;
  }) => post<{
    proposed: typeof body;
    estimatedNotional: number;
    preflightViolations: Violation[];
    passesRules: boolean;
    cashAfter: number;
    warnings: string[];
    reviewToken: string;
  }>('/api/orders/preview', body),
};
